<script setup lang="ts">
    import { computed, ref } from 'vue';
    import { __ } from '@/js/stores/expressions';
    import SettingsPageLayout from '@/js/components/settings/SettingsPageLayout.vue';
    import LoadingDots from '@/js/components/LoadingDots.vue';
    import EchoButton from '@/js/components/EchoButton.vue';
    import FormGroup from '@/js/components/FormGroup.vue';
    import InputLabel from '@/js/components/InputLabel.vue';
    import TextInput, { TextInputWidth } from '@/js/components/TextInput.vue';
    import SelectInput from '@/js/components/SelectInput.vue';
    import SortArrows from '../components/SortArrows.vue';
    import { useInternalUsersTable } from '@/js/composables/useInternalUsersTable';
    import IconButton from '@/js/components/IconButton.vue';
    import { useSuccessDialog } from '@/js/stores/success-dialog';
    import { useConfirmationDialog } from '@/js/stores/confirmationDialog';
    import { useGetMyAccount } from '@/js/hooks/user';
    import { PlusIcon, CheckIcon, TrashIcon } from '@lucide/vue';
    import {
        useQueryGetSysOpUsers,
        useMutationCreateSysOpUser,
        useMutationChangeUserRole,
        useMutationDeleteSysOpUser,
    } from '@/js/services/api/backOffice/backOfficeQueries';

    // Must be kept in sync with RoleEntity.SysOpId / RoleEntity.MemberId in
    // backend/src/Echo.DataAccessLayer/Entities/Echo/RoleEntity.cs.
    const SYSOP_ROLE_ID = '13371337-1337-1337-1337-133713371337';
    const MEMBER_ROLE_ID = 'abc123ab-c123-abc1-23ab-c123abc123ab';

    // New SysOp users only ever go into this one tenant (same on local/dev/prod). Must be kept in
    // sync with CustomerEntity.SysOpTenantId in backend/src/Echo.DataAccessLayer/Entities/Master/CustomerEntity.cs
    // — the backend enforces this independently, so a mismatch here just shows an error rather than being a security gap.
    const SYSOP_TENANT_ID = 'ccb49594-7cc6-45a5-9327-fb0e2066be5e';

    const successDialogStore = useSuccessDialog();

    const { data: sysOpUsersData, isLoading } = useQueryGetSysOpUsers();

    type Row = {
        tenantId: string;
        tenantName: string;
        userId: string;
        email: string;
        name: string;
        roleName: string;
        isSysOp: boolean;
        emailVerified: boolean;
    };

    const rows = computed<Row[]>(() => {
        const entries = sysOpUsersData.value ?? {};
        return Object.entries(entries).flatMap(([tenantName, entry]) =>
            entry.users.map(({ user, emailVerified }) => ({
                tenantId: entry.tenantId,
                tenantName,
                userId: user.id,
                email: user.email ?? '',
                name: [user.firstName, user.lastName].filter(Boolean).join(' '),
                roleName: user.role?.name ?? '',
                isSysOp: user.role?.id === SYSOP_ROLE_ID,
                emailVerified,
            }))
        );
    });

    const { search, tenantFilter, tenantNames, setSort, activeSort, visibleRows } =
        useInternalUsersTable(rows);

    const columns = computed<{ field: keyof Row; label: string }[]>(() => [
        { field: 'tenantName', label: __('back-office-users-col-tenant') },
        { field: 'name', label: __('users-table-column-name') },
        { field: 'email', label: __('email') },
        { field: 'roleName', label: __('back-office-users-col-role') },
        { field: 'emailVerified', label: __('verified') },
        { field: 'isSysOp', label: __('back-office-users-col-sysop') },
    ]);

    const { mutateAsync: changeUserRoleMutation, isPending: isChangingRole } =
        useMutationChangeUserRole();

    // Toggling off always demotes to MEMBER_ROLE_ID, with no way to restore the row's prior role. Only
    // safe for a verified row already in SYSOP_TENANT_ID, so the template disables the checkbox for any
    // other row (a client-tenant Admin shown here only for their email, or a not-yet-verified new user).
    const toggleSysOp = async (row: Row) => {
        try {
            await changeUserRoleMutation({
                tenantId: row.tenantId,
                userId: row.userId,
                roleId: row.isSysOp ? MEMBER_ROLE_ID : SYSOP_ROLE_ID,
            });
        } catch {
            // Error displayed via the mutation's onError handler (showSysOpUserError) in backOfficeQueries.ts
        }
    };

    const { data: myAccount } = useGetMyAccount();
    const isMe = (row: Row) => row.email.toLowerCase() === myAccount.value?.email.toLowerCase();

    const confirm = useConfirmationDialog();
    const { mutateAsync: deleteSysOpUserMutation, isPending: isDeletingUser } =
        useMutationDeleteSysOpUser();

    const confirmDeleteUser = async (row: Row) => {
        const confirmed = await confirm.openDialog({
            title: __('confirm-delete-user-title'),
            message: __('confirm-delete-user-message', {
                name: `${[row.name, row.email].filter(Boolean).join(', ')} (${row.tenantName})`,
            }),
            confirmText: __('yes-confirm-delete'),
            cancelText: __('no'),
            confirmIcon: TrashIcon,
        });
        if (!confirmed) return;

        try {
            await deleteSysOpUserMutation({ tenantId: row.tenantId, userId: row.userId });
        } catch {
            // Error displayed via the mutation's onError handler (showSysOpUserError) in backOfficeQueries.ts
        }
    };

    // Add user: always into SYSOP_TENANT_ID with the Member role. A brand-new identity starts unverified,
    // and SysOp can only be granted to an already-verified @indicateme.se identity, so it only becomes
    // reachable afterward, via the table toggle.
    const showAddUserForm = ref(false);
    const addUserEmail = ref('');
    const addUserFirstName = ref('');
    const addUserLastName = ref('');

    const canCreateUser = computed(
        () =>
            !!addUserEmail.value.trim() &&
            !!addUserFirstName.value.trim() &&
            !!addUserLastName.value.trim()
    );

    const { mutateAsync: createSysOpUserMutation, isPending: isCreatingUser } =
        useMutationCreateSysOpUser();

    const submitAddUser = async () => {
        if (!canCreateUser.value) return;
        try {
            await createSysOpUserMutation({
                tenantId: SYSOP_TENANT_ID,
                email: addUserEmail.value.trim(),
                firstName: addUserFirstName.value.trim(),
                lastName: addUserLastName.value.trim(),
                roleId: MEMBER_ROLE_ID,
            });
        } catch {
            // Error displayed via the mutation's onError handler (showSysOpUserError) in backOfficeQueries.ts
            return;
        }
        addUserEmail.value = '';
        addUserFirstName.value = '';
        addUserLastName.value = '';
        showAddUserForm.value = false;
        successDialogStore.openDialog({ message: __('back-office-sysop-users-add-user-success') });
    };
</script>
<template>
    <SettingsPageLayout>
        <template #header>
            <h1 class="heading-lg-light">{{ __('back-office-sysop-users') }}</h1>
        </template>
        <template #topbar>
            <EchoButton variant="primary" @click="showAddUserForm = !showAddUserForm">
                {{ __('back-office-sysop-users-add-user') }}
                <template #icon-right><PlusIcon /></template>
            </EchoButton>
        </template>
        <template #content>
            <form
                v-if="showAddUserForm"
                class="w-lg max-w-full mb-8"
                @submit.prevent="submitAddUser"
            >
                <FormGroup>
                    <div>
                        <InputLabel for="sysop-users-add-email">{{ __('email') }}</InputLabel>
                        <TextInput
                            id="sysop-users-add-email"
                            v-model="addUserEmail"
                            :width="TextInputWidth.Full"
                        />
                    </div>
                    <div>
                        <InputLabel for="sysop-users-add-first-name">
                            {{ __('user-first-name') }}
                        </InputLabel>
                        <TextInput
                            id="sysop-users-add-first-name"
                            v-model="addUserFirstName"
                            :width="TextInputWidth.Full"
                        />
                    </div>
                    <div>
                        <InputLabel for="sysop-users-add-last-name">
                            {{ __('user-last-name') }}
                        </InputLabel>
                        <TextInput
                            id="sysop-users-add-last-name"
                            v-model="addUserLastName"
                            :width="TextInputWidth.Full"
                        />
                    </div>
                </FormGroup>
                <div class="flex gap-3 mt-6">
                    <EchoButton
                        type="submit"
                        variant="primary"
                        :disabled="!canCreateUser"
                        :loading="isCreatingUser"
                    >
                        {{ __('save') }}
                        <template #icon-right><CheckIcon /></template>
                    </EchoButton>
                </div>
            </form>

            <div class="flex flex-wrap items-center gap-3 mb-6">
                <TextInput
                    v-model="search"
                    :width="TextInputWidth.Large"
                    :placeholder="__('back-office-users-search-placeholder')"
                    :aria-label="__('search')"
                />
                <SelectInput
                    v-model="tenantFilter"
                    :aria-label="__('back-office-users-col-tenant')"
                >
                    <option value="">{{ __('back-office-users-all-tenants') }}</option>
                    <option v-for="tenant in tenantNames" :key="tenant" :value="tenant">
                        {{ tenant }}
                    </option>
                </SelectInput>
            </div>
            <p class="mb-4 text-caption text-gray-medium">
                {{ __('back-office-sysop-users-count', { count: String(visibleRows.length) }) }}
            </p>
            <div class="overflow-x-auto">
                <table class="w-full border-collapse">
                    <thead>
                        <tr class="border-b border-gray-lightest">
                            <th
                                v-for="col in columns"
                                :key="col.field"
                                class="py-3 pl-3 pr-6 text-left text-caption-bold uppercase"
                            >
                                <span class="flex items-center gap-1">
                                    {{ col.label }}
                                    <SortArrows
                                        :asc-disabled="activeSort(col.field) === 'asc'"
                                        :desc-disabled="activeSort(col.field) === 'desc'"
                                        @sort="(dir) => setSort(col.field, dir)"
                                    />
                                </span>
                            </th>
                            <th />
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-if="isLoading">
                            <td colspan="7" class="py-12 text-center"><LoadingDots /></td>
                        </tr>
                        <template v-else>
                            <tr v-if="!visibleRows.length">
                                <td colspan="7" class="py-6 text-body text-gray-medium text-center">
                                    {{ __('back-office-sysop-users-empty') }}
                                </td>
                            </tr>
                            <tr
                                v-for="row in visibleRows"
                                :key="row.userId"
                                class="border-b border-gray-lightest"
                            >
                                <td class="py-3 pl-3 pr-6 text-body">{{ row.tenantName }}</td>
                                <td class="py-3 pl-3 pr-6 text-body">{{ row.name }}</td>
                                <td class="py-3 pl-3 pr-6 text-body">{{ row.email }}</td>
                                <td class="py-3 pl-3 pr-6 text-body">{{ row.roleName }}</td>
                                <td class="py-3 pl-3 pr-6 text-body">
                                    {{ row.emailVerified ? __('yes') : __('no') }}
                                </td>
                                <td class="py-3 pl-3 pr-6">
                                    <input
                                        type="checkbox"
                                        class="Custom-checkbox cursor-pointer"
                                        :checked="row.isSysOp"
                                        :disabled="
                                            isChangingRole ||
                                            row.tenantId !== SYSOP_TENANT_ID ||
                                            !row.emailVerified
                                        "
                                        @change="toggleSysOp(row)"
                                    />
                                </td>
                                <td class="py-3 pl-3 pr-3 text-right">
                                    <IconButton
                                        size="size-6"
                                        area-size="size-6"
                                        :tooltip="__('delete')"
                                        :disabled="isDeletingUser || isMe(row)"
                                        @click="confirmDeleteUser(row)"
                                    >
                                        <TrashIcon class="size-5" />
                                    </IconButton>
                                </td>
                            </tr>
                        </template>
                    </tbody>
                </table>
            </div>
        </template>
    </SettingsPageLayout>
</template>
